import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { FormattedMessage } from '../../i18n/FormattedMessage';
import { getDateFormat } from '../../utils/utils';
import { AriesOutlinePrimaryButton } from '../../components_2/AriesButton';
import coachMarks from '../../libs/constants/coachMarks';

/**
 * My Jobs list row. Same columns/shape as the Jobs Due row (the list is scoped
 * to the current user's Assigned jobs, so every row maps to "Not Started" and
 * offers the Start action only, open to a future "View"). Preserves the
 * first-row Start-job onboarding coachmark and the recurring task-count badge.
 */
const getStatusLabel = (jobStatus) =>
  jobStatus === 'Inprogress' ? 'In Progress' : 'Not Started';

const getStatusClassName = (jobStatus) =>
  jobStatus === 'Inprogress' ? 'aries-status-active' : 'aries-status-inactive';

const getAssignedTo = (technicians) => {
  if (!technicians?.length) {
    return '-';
  }
  return technicians.map((tech) => tech.customerName).join(', ');
};

const MyJobsRowAries = (props) => {
  const {
    row,
    index,
    handleStartJob,
    showTaskModdal,
    runCoachMarks,
    currentCoachmarkTarget,
    toggleCoachmarksVisibility,
  } = props;
  const {
    _id,
    jobId,
    createdFor,
    reportCategory,
    recurringInspectionDetails,
    startDateTime,
    type,
    price,
    technicians,
    jobStatus,
  } = row;

  const frequency = recurringInspectionDetails?.frequency;
  const startJobPayload = {
    jobId: _id,
    createdFor,
    reportCategory,
    type,
    price,
  };
  if (frequency) {
    startJobPayload.frequency = frequency;
  }

  const taskCount = recurringInspectionDetails?.tasks?.length ?? 0;
  const isFirstRow = index === 0;
  const isStartJobCoachmark =
    currentCoachmarkTarget === coachMarks.inspectionReminders.startJob.target;

  useEffect(() => {
    if (isFirstRow && runCoachMarks) runCoachMarks(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const advanceCoachmark = () => {
    if (isStartJobCoachmark) {
      toggleCoachmarksVisibility?.(
        null,
        null,
        coachMarks.inspectionReminders.startJob.nextTarget
      );
    }
  };

  return (
    <tr key={_id}>
      <td className="aries-my-jobs-jobid aries-my-jobs-field">
        <h3>
          <FormattedMessage id="Job Id" defaultMessage="Job Id" />
        </h3>
        <span>{jobId}</span>
      </td>
      <td className="aries-my-jobs-sitename">
        <h3>
          <FormattedMessage id="Site Name" defaultMessage="Site Name" />
        </h3>
        <span>{createdFor?.businessName}</span>
      </td>
      <td className="aries-my-jobs-field">
        <h3>
          <FormattedMessage id="Job Type" defaultMessage="Job Type" />
        </h3>
        <FormattedMessage
          id={reportCategory ?? ' '}
          defaultMessage={reportCategory ?? ' '}
        />
      </td>
      <td className="aries-my-jobs-field">
        <h3>
          <FormattedMessage id="Job Kind" defaultMessage="Job Kind" />
        </h3>
        <FormattedMessage id={type ?? ' '} defaultMessage={type ?? ' '} />
      </td>
      <td className="aries-my-jobs-field">
        <h3>
          <FormattedMessage id="Assigned To" defaultMessage="Assigned To" />
        </h3>
        <span>{getAssignedTo(technicians)}</span>
      </td>
      <td className="aries-my-jobs-field">
        <h3>
          <FormattedMessage id="Due Date" defaultMessage="Due Date" />
        </h3>
        <span>{moment(startDateTime).format(getDateFormat())}</span>
      </td>
      <td className="aries-card-status">
        <h3>
          <FormattedMessage id="Status" defaultMessage="Status" />
        </h3>
        <span className={getStatusClassName(jobStatus)}>
          <FormattedMessage
            id={getStatusLabel(jobStatus)}
            defaultMessage={getStatusLabel(jobStatus)}
          />
        </span>
      </td>
      <td
        className={`aries-my-jobs-action${
          isFirstRow ? ' start-job-coachmark' : ''
        }`}
        onClick={advanceCoachmark}
      >
        {isFirstRow && isStartJobCoachmark && (
          <div
            id="test-tooltip"
            className={`info-timecard-info-show-descr coachmark-tooltips ${coachMarks.inspectionReminders.startJob.toolTipClass}`}
            style={coachMarks.inspectionReminders.startJob.style}
          >
            {coachMarks.inspectionReminders.startJob.content}
          </div>
        )}
        {taskCount > 0 && (
          <button
            type="button"
            className="aries-my-jobs-task-badge"
            onClick={() => showTaskModdal(recurringInspectionDetails.tasks)}
          >
            {taskCount}
          </button>
        )}
        <AriesOutlinePrimaryButton
          size="sm"
          text="Start"
          onClick={() => handleStartJob(startJobPayload)}
        />
      </td>
    </tr>
  );
};

MyJobsRowAries.propTypes = {
  row: PropTypes.object,
  index: PropTypes.number,
  handleStartJob: PropTypes.func,
  showTaskModdal: PropTypes.func,
  runCoachMarks: PropTypes.func,
  currentCoachmarkTarget: PropTypes.string,
  toggleCoachmarksVisibility: PropTypes.func,
};

export default MyJobsRowAries;
